import { CRAFTING_RECIPES } from './crafting/recipes';
import { addInventoryItem, playerState, refreshHud, type InventorySlot } from './player-state';
import { isBackendEnabled, queueSync, syncEquipWeapon } from '../net/backend';

export type EquipFilter = 'head' | 'body' | 'weapon';

const DEFAULT_WEAPON = 'WoodClub';
const MIN_INVENTORY_SLOTS = 9;

const HEAD_ITEMS = new Set(['Pink Hunter Fur Shoulderpads']);

const BODY_ITEMS = new Set(['Pink Hunter Fur', 'Unfinished Pink Hunter Fur Coat']);

const KNOWN_WEAPONS = new Set(['WoodClub', 'Nerd Weapon', 'Boogatooth Lure']);

let inventoryFilter: EquipFilter | null = null;

export function getInventoryFilter() {
  return inventoryFilter;
}

export function setInventoryFilter(filter: EquipFilter | null) {
  inventoryFilter = filter;
}

export function clearInventoryFilter() {
  inventoryFilter = null;
}

function isDefaultWoodClub(slot: InventorySlot | null) {
  return Boolean(slot?.isDefault && slot.name === DEFAULT_WEAPON);
}

function isWeaponItem(name: string) {
  if (KNOWN_WEAPONS.has(name)) return true;
  const recipe = CRAFTING_RECIPES.find((r) => r.result === name);
  if (!recipe) return false;
  if (recipe.isArmor) return false;
  if (recipe.isWeapon === false) return false;
  return true;
}

export function getItemEquipType(name: string): EquipFilter | null {
  if (!name) return null;
  const recipe = CRAFTING_RECIPES.find((r) => r.result === name);
  if (recipe) {
    if (recipe.isWeapon === false) return null;
    if (recipe.isArmor) {
      return name.includes('Shoulderpads') ? 'head' : 'body';
    }
    return 'weapon';
  }
  if (HEAD_ITEMS.has(name)) return 'head';
  if (BODY_ITEMS.has(name)) return 'body';
  if (isWeaponItem(name)) return 'weapon';
  return null;
}

export function itemMatchesEquipFilter(name: string, filter: EquipFilter | null) {
  if (!filter || !name) return true;
  if (filter === 'head') return HEAD_ITEMS.has(name);
  if (filter === 'body') return BODY_ITEMS.has(name);
  return isWeaponItem(name);
}

function collectMatchingSlots(filter: EquipFilter) {
  const matching: Array<{ index: number; data: InventorySlot }> = [];
  for (let i = 0; i < playerState.inventorySlots.length; i++) {
    const slotData = playerState.inventorySlots[i];
    if (slotData?.count && itemMatchesEquipFilter(slotData.name, filter)) {
      matching.push({ index: i, data: slotData });
    }
  }
  if (filter === 'weapon') {
    matching.sort((a, b) => {
      const aDefault = a.data.name === DEFAULT_WEAPON;
      const bDefault = b.data.name === DEFAULT_WEAPON;
      if (aDefault && !bDefault) return -1;
      if (!aDefault && bDefault) return 1;
      return a.index - b.index;
    });
  } else {
    matching.sort((a, b) => a.index - b.index);
  }
  return matching;
}

function buildFilteredDisplayPlan(filter: EquipFilter, minSlots: number) {
  const matching = collectMatchingSlots(filter);
  const emptyQueue: number[] = [];
  for (let i = 0; i < playerState.inventorySlots.length; i++) {
    if (!playerState.inventorySlots[i]) emptyQueue.push(i);
  }

  const plan: Array<{ slotIndex: number; data: { name: string; count: number } | null }> = [];
  for (let displayIdx = 0; displayIdx < minSlots; displayIdx++) {
    if (displayIdx < matching.length) {
      plan.push({ slotIndex: matching[displayIdx].index, data: matching[displayIdx].data });
      continue;
    }
    let slotIndex = emptyQueue.shift();
    if (slotIndex === undefined) {
      slotIndex = playerState.inventorySlots.length;
      playerState.inventorySlots.push(null);
    }
    plan.push({ slotIndex, data: null });
  }
  return plan;
}

export function getInventoryDisplaySlots() {
  const targetSlotsCount = Math.max(
    MIN_INVENTORY_SLOTS,
    Math.ceil(playerState.inventorySlots.length / 3) * 3,
  );
  while (playerState.inventorySlots.length < targetSlotsCount) {
    playerState.inventorySlots.push(null);
  }

  if (inventoryFilter) {
    return buildFilteredDisplayPlan(inventoryFilter, MIN_INVENTORY_SLOTS);
  }

  return playerState.inventorySlots.map((data, slotIndex) => ({
    slotIndex,
    data: data ? { name: data.name, count: data.count } : null,
  }));
}

function deductInventorySlot(slotIndex: number, itemName: string) {
  const slot = playerState.inventorySlots[slotIndex];
  if (!slot || slot.name !== itemName || isDefaultWoodClub(slot)) return;
  if (slot.count > 1) slot.count -= 1;
  else playerState.inventorySlots[slotIndex] = null;
}

export function equipItemToSlot(slotType: EquipFilter, itemName: string, inventorySlotIndex: number) {
  if (getItemEquipType(itemName) !== slotType) return false;

  let oldItem: string | null = null;
  if (slotType === 'head') oldItem = playerState.equipped.head;
  if (slotType === 'body') oldItem = playerState.equipped.body;
  if (slotType === 'weapon') oldItem = playerState.equipped.weapon;

  if (oldItem) addInventoryItem(oldItem, 1);

  if (slotType === 'head') playerState.equipped.head = itemName;
  if (slotType === 'body') playerState.equipped.body = itemName;
  if (slotType === 'weapon') playerState.equipped.weapon = itemName;

  deductInventorySlot(inventorySlotIndex, itemName);
  refreshHud();
  if (slotType === 'weapon' && isBackendEnabled()) {
    syncEquipWeapon(itemName);
  } else if (isBackendEnabled()) {
    queueSync('full');
  }
  return true;
}

export function unequipSlot(slotType: 'head' | 'body') {
  let itemName: string | null = null;
  if (slotType === 'head') itemName = playerState.equipped.head;
  if (slotType === 'body') itemName = playerState.equipped.body;
  if (!itemName) return false;

  if (slotType === 'head') playerState.equipped.head = null;
  if (slotType === 'body') playerState.equipped.body = null;

  addInventoryItem(itemName, 1);
  refreshHud();
  return true;
}

export function tryEquipOnInventoryTap(slotIndex: number) {
  const filter = inventoryFilter;
  if (!filter) return false;

  const slotData = playerState.inventorySlots[slotIndex];
  if (!slotData?.count) return false;

  return equipItemToSlot(filter, slotData.name, slotIndex);
}
